import { makeAutoObservable, action } from "mobx";
import Highcharts from "highcharts";

export interface ZoomRange {
	min: number | null;
	max: number | null;
}

export class ZoomStore {
	range: ZoomRange = { min: null, max: null };
	private chart: Highcharts.Chart | null = null;
	private onZoomChange?: (range: ZoomRange) => void;

	constructor(onZoomChange?: (range: ZoomRange) => void) {
		this.onZoomChange = onZoomChange;
		makeAutoObservable(this, {
			updateChart: action,
			updateRange: action,
			handleZoomChange: action,
			zoomTo: action,
			zoomIn: action,
			zoomOut: action,
			reset: action,
		});
	}

	updateChart(chart: Highcharts.Chart | null) {
		this.chart = chart;
	}

	updateRange(min: number | null, max: number | null) {
		this.range = { min, max };
		this.onZoomChange?.(this.range);
	}

	handleZoomChange(event: Highcharts.AxisSetExtremesEventObject) {
		const isZoomEvent = event.trigger === "zoom" || event.trigger === "navigator";
		const isResetEvent = event.trigger === "reset" || (event.min === undefined && event.max === undefined);

		if (isZoomEvent) {
			this.updateRange(event.min ?? null, event.max ?? null);
		} else if (isResetEvent) {
			this.updateRange(null, null);
		}
	}

	zoomTo(minValue: number, maxValue: number) {
		this.updateRange(minValue, maxValue);
		if (this.chart) {
			this.chart.xAxis[0].setExtremes(minValue, maxValue);
		}
	}

	zoomIn(factor: number = 0.25) {
		if (!this.chart) return;

		const axis = this.chart.xAxis[0];
		const extremes = axis.getExtremes();
		const currentMin = extremes.min ?? extremes.dataMin;
		const currentMax = extremes.max ?? extremes.dataMax;
		const visibleRange = currentMax - currentMin;
		const center = (currentMin + currentMax) / 2;
		const newRange = visibleRange * (1 - factor);
		const newMin = center - newRange / 2;
		const newMax = center + newRange / 2;

		this.zoomTo(newMin, newMax);
	}

	zoomOut(factor: number = 0.25) {
		if (!this.chart) return;

		const axis = this.chart.xAxis[0];
		const extremes = axis.getExtremes();
		const currentMin = extremes.min ?? extremes.dataMin;
		const currentMax = extremes.max ?? extremes.dataMax;
		const visibleRange = currentMax - currentMin;
		const center = (currentMin + currentMax) / 2;
		const newRange = visibleRange * (1 + factor);
		const newMin = Math.max(extremes.dataMin, center - newRange / 2);
		const newMax = Math.min(extremes.dataMax, center + newRange / 2);

		// If we've reached full extent, reset zoom
		if (newMin <= extremes.dataMin && newMax >= extremes.dataMax) {
			this.reset();
		} else {
			this.zoomTo(newMin, newMax);
		}
	}

	reset() {
		this.updateRange(null, null);
		if (this.chart) {
			this.chart.xAxis[0].setExtremes(undefined, undefined);
		}
	}

	get isZoomed(): boolean {
		return this.range.min !== null || this.range.max !== null;
	}
}
